/**
 * Shared client-side access to the curated speaker cover pool
 * (`/api/speaker-cover-pool`). Loads once per page, deduped across all
 * consumers via a module-level cache.
 *
 * RULE 20 (Feb 08, 2026): every speaker-tile / picture-library / topic /
 * home-grid surface MUST use this pool as its cover source, NOT the
 * translucent `/api/portrait/cutout/...` PNG. The cutout stays only for
 * the speaker's OWN /speaker page (per rule 19).
 */
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const state = { pool: null, poolNorm: null, inFlight: null };
const subscribers = new Set();
const notify = () => subscribers.forEach((cb) => cb());

const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

// Levenshtein-lite (capped) — cheap enough to run against a few hundred
// pool keys once per unresolved speaker lookup.
const levDist = (a, b, cap = 3) => {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > cap) return cap + 1;
  if (!m) return n; if (!n) return m;
  let prev = Array(n + 1).fill(0);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    const curr = Array(n + 1).fill(0);
    curr[0] = i;
    let rowMin = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > cap) return cap + 1;
    prev = curr;
  }
  return prev[n];
};

/** Find the best fuzzy pool key for a speaker name that missed exact +
 *  normalized lookup. Tries `contains` first (either direction), then
 *  Levenshtein within a small cap. Returns the pool key or null. */
const fuzzyLookup = (poolNorm, targetNorm) => {
  if (!poolNorm || !targetNorm) return null;
  const keys = Object.keys(poolNorm);
  // Contains-based
  for (const k of keys) {
    if (k.length < 6 || targetNorm.length < 6) continue;
    if (k.includes(targetNorm) || targetNorm.includes(k)) return k;
  }
  // Levenshtein
  const cap = Math.max(2, Math.floor(targetNorm.length / 6));
  let best = null;
  let bestD = cap + 1;
  for (const k of keys) {
    if (Math.abs(k.length - targetNorm.length) > cap) continue;
    const d = levDist(targetNorm, k, cap);
    if (d < bestD) { bestD = d; best = k; if (d === 0) break; }
  }
  return best;
};

const loadOnce = () => {
  if (state.inFlight) return state.inFlight;
  state.inFlight = (async () => {
    try {
      // Feb-2026 fix (user "why are the select speakers who have
      // folders matching theyre names not displaying the pictures in
      // their folders as cover images"): this endpoint returns
      // `Access-Control-Allow-Origin: *`. With `credentials: 'include'`
      // the browser rejects any wildcard-origin response, so the fetch
      // silently failed and `state.pool` stayed empty — leaving every
      // speaker (Henach included) without their random 1IDI65 covers.
      // The pool is a public, credential-less GET. Drop `credentials`.
      const res = await fetch(`${API}/api/speaker-cover-pool`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      state.pool = data.pool || {};
      state.poolNorm = {};
      for (const [name, ids] of Object.entries(state.pool)) {
        state.poolNorm[norm(name)] = ids;
      }
      // Feb-2026 v220.1 (user "this is a good fix but takes a long
      // time for images to apear please fix and make entire website
      // much faster"): the previous precache posted
      // `/api/gallery/photo/{fid}?size=400` URLs — but `pickCover()`
      // renders `/api/portrait/by-file-id/{fid}?original=1`. DIFFERENT
      // endpoints, so the SW cache was warmed with URLs nobody ever
      // requested. Every cover load stayed cold. Fixed by precaching
      // the exact URL `pickCover` returns.
      //
      // Also defer to `requestIdleCallback` so 120 background fetches
      // don't compete with above-the-fold LCP portraits on a cold
      // cache. Fires ~50 ms after the browser goes idle; falls back
      // to `setTimeout(1200)` on Safari (no `rIC` support).
      const schedulePrecache = (fn) => {
        if (typeof requestIdleCallback === 'function') {
          requestIdleCallback(fn, { timeout: 2500 });
        } else {
          setTimeout(fn, 1200);
        }
      };
      schedulePrecache(() => {
        try {
          const urls = [];
          const seen = new Set();
          for (const ids of Object.values(state.pool)) {
            if (!Array.isArray(ids)) continue;
            for (const fid of ids) {
              if (!fid || seen.has(fid)) continue;
              seen.add(fid);
              // Match the URL shape from pickCover() exactly so SW
              // IMG_CACHE hits on subsequent renders.
              urls.push(`${API}/api/portrait/by-file-id/${fid}?size=400`);
              if (urls.length >= 120) break;
            }
            if (urls.length >= 120) break;
          }
          if (urls.length && navigator.serviceWorker && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'PRECACHE_MOSAIC_TILES',
              urls,
            });
          }
        } catch { /* SW unavailable — ignore */ }
      });
    } catch {
      state.pool = state.pool || {};
      state.poolNorm = state.poolNorm || {};
    } finally {
      state.inFlight = null;
      notify();
    }
    return state.pool;
  })();
  return state.inFlight;
};

/** Warm the cover pool as early as possible so the first RecordingCard
 *  / folder icon paint already has the pool ready. Called from App.js
 *  on mount (Feb 2026 user "make the images actually show up"). */
export const warmCoverPool = () => loadOnce();

/** Return how many pool images the speaker has (0 if not in pool).
 *  Used by SpeakerPage to decide bullet-arrow layout — a speaker with
 *  only 1 pool image has no variety, so their own page falls back to
 *  bullet arrows to avoid the same portrait repeating on every tile. */
export const poolCount = (speaker) => {
  const pool = state.pool;
  if (!pool || !speaker) return 0;
  let ids = pool[speaker];
  if (!ids) {
    const nn = norm(speaker);
    ids = (state.poolNorm && state.poolNorm[nn]) || null;
    if (!ids) {
      const fk = fuzzyLookup(state.poolNorm, nn);
      if (fk) ids = state.poolNorm[fk];
    }
  }
  return ids ? ids.length : 0;
};

// Feb-2026 (user "when i said i want randomized pic i meant the
// permanent rule of how to use ALL pics in the folders i told you at
// random so i see all the pics there randomly not one random for
// every one"): the previous implementation hashed `key + speaker` and
// modded into the pool once, forever — so every card was locked to
// ONE pool pic on every device, every visit. Users only ever saw
// ~12 of the ~20 pool pics because of hash collisions, and cards
// never rotated. Rule 21 explicitly wants the WHOLE pool visible
// over time.
//
// Two-part fix:
//   1. `SESSION_SALT` — a random 32-bit int minted once per page load
//      (persisted to `sessionStorage` so it survives in-app SPA
//      navigations but resets on real reloads and new tabs). Hash
//      input is now `key + speaker + salt`, so the SAME card lands
//      on a DIFFERENT pool pic every visit. Over ~ceil(pool_size /
//      cards_per_speaker) visits, users see every single face.
//   2. Per-render anti-collision shuffle — inside a single render, we
//      lay the pool out as a per-session-shuffled cycle so the FIRST
//      N cards (up to pool size) get UNIQUE pics. Card N+1 wraps back
//      to the start. This guarantees a 30-card page from a 20-pic
//      pool shows all 20 unique faces on every load.
// Feb-2026 (user "on mobile its not showing the randomized dif pics as
// cover pics"): the old design used a `sessionStorage`-stored random
// salt minted once per module load. Desktop reloads regularly so the
// salt rotates and users see the whole pool. On mobile (Median.co
// WebView / iOS PWA), the WebView is kept alive across app switches
// forever — `sessionStorage` never clears, module never re-inits, and
// the same salt lives for days → same card, same pic, every visit.
//
// New design: the salt is a TIME SLOT that ticks over every ~10 min so
// mobile clients rotate the whole pool automatically as they navigate,
// AND we bump `slotEpoch` when the tab returns to foreground so
// long-backgrounded apps get a visible reshuffle on resume.
const SALT_SLOT_MS = 10 * 60 * 1000; // 10 minutes per slot
let _slotEpoch = 0;
const currentSalt = () => (((Date.now() / SALT_SLOT_MS) | 0) + _slotEpoch) >>> 0;

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // Fresh spin every foreground return.
      _slotEpoch = (_slotEpoch + 1) >>> 0;
      shuffleCache.clear();
      notify();
    }
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      _slotEpoch = (_slotEpoch + 1) >>> 0;
      shuffleCache.clear();
      notify();
    }
  });
}

// Cache the per-render shuffled pool per (speaker, salt) so multiple
// calls within the same session don't reshuffle on every keystroke.
const shuffleCache = new Map();
const shufflePool = (ids, salt) => {
  const cacheKey = `${salt}:${ids.length}:${ids[0]}:${ids[ids.length - 1]}`;
  const cached = shuffleCache.get(cacheKey);
  if (cached) return cached;
  // Fisher-Yates seeded by (salt + i) — deterministic for a given
  // session so a card that took slot #3 keeps it on re-render, but
  // fresh across sessions.
  const arr = ids.slice();
  let seed = salt >>> 0;
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0x100000000;
  };
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  shuffleCache.set(cacheKey, arr);
  return arr;
};

/** Rule 21-compliant cover picker: stable-per-session (no flicker on
 *  re-render), rotating-across-sessions (users see the whole pool),
 *  collision-minimizing within a single grid render.
 *
 *  Same `speaker + key` on the same visit → same pool pic (deterministic
 *  within session so React re-renders don't shuffle covers under the
 *  user). Reload → fresh session-salt → whole pool re-distributed.
 */
export const pickCover = (speaker, key = '') => {
  const pool = state.pool;
  if (!pool || !speaker) return null;
  let ids = pool[speaker];
  if (!ids || !ids.length) {
    const nn = norm(speaker);
    ids = (state.poolNorm && state.poolNorm[nn]) || null;
    if (!ids || !ids.length) {
      // Fuzzy fallback: match against near-normalized pool keys so
      // "HaRav Henach Leibowitz Ztzl" still finds "HaRav Henach
      // Leibowitz Zt'l" and "R' Bentzion Shafier" finds "R' Benzion
      // Shafier".
      const fk = fuzzyLookup(state.poolNorm, nn);
      if (fk) ids = state.poolNorm[fk];
    }
  }
  if (!ids || !ids.length) return null;
  // Time-slotted salt (see block above): rotates every 10 min AND on
  // foreground-return, so mobile WebViews reshuffle the pool over the
  // course of ordinary browsing.
  const salt = currentSalt();
  const s = `${key}::${speaker}::${salt}`;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  // Per-slot-shuffled pool — first N hash outputs land on N unique pool pics.
  const shuffled = shufflePool(ids, salt);
  const fid = shuffled[Math.abs(h) % shuffled.length];
  // Feb-2026 v220.1 (user "takes a long time for images to appear"):
  // request the 400 px resized JPEG instead of the 60-90 KB original.
  // Backend Pillow-resizes + caches to object store, so subsequent
  // requests are 5-15 KB thumbs. Tile grids are always <=400 px wide.
  return `${API}/api/portrait/by-file-id/${fid}?size=400`;
};

export default function useCoverPool() {
  const [ready, setReady] = useState(!!state.pool);
  useEffect(() => {
    let alive = true;
    const update = () => { if (alive) setReady(!!state.pool); };
    subscribers.add(update);
    loadOnce().then(update);
    return () => { alive = false; subscribers.delete(update); };
  }, []);
  return { ready, pickCover };
}
